/**
 * entityIdByReferenceKey
 *
 * Walks a form layout and maps each entity-reference storage key
 * (`entityGroupId || entityId`, the key under which a selection is persisted in
 * `entityReferences`) to its real `entityId`. The layout is the authority for
 * this mapping: every `UIEntityReference` / `UIEmbeddedEntity` element carries
 * both ids, whereas a grouped reference's stored map key is only the group id.
 *
 * Passed to `entityReferenceIndex` so the `allEntityIdsAndGroupIds` index can
 * recover the entity id of grouped references whose persisted entry predates the
 * denormalized `entityId` field, rather than collapsing them onto the group id.
 */
import { UIElement, UIElementType, UIEntityReference, UIEmbeddedEntity } from '#types/System.types';

export const entityIdByReferenceKey = (structure?: UIElement): Record<string, string> => {
    const map: Record<string, string> = {};
    const visit = (element?: UIElement): void => {
        if (!element) return;
        if (
            element.uiElementType === UIElementType.EntityReference ||
            element.uiElementType === UIElementType.EmbeddedEntity
        ) {
            const ref = element as UIEntityReference | UIEmbeddedEntity;
            if (ref.entityId) map[ref.entityGroupId || ref.entityId] = ref.entityId;
        } else if ('children' in element) {
            element.children.forEach(visit);
        }
    };
    visit(structure);
    return map;
};
